import { inject, reactive, watch } from "vue";
import { AUTOFILL_PORT } from "@/shared/contracts/ports";
import {
	type CopyFromSource,
	type EntityDefinition,
	type FieldDefinition,
	liveFields,
} from "@/shared/contracts/structure";

function isEmpty(value: unknown): boolean {
	return value === null || value === undefined || value === "";
}

/** `config.copyFrom` é uma origem ou uma lista delas; daqui para a frente, sempre lista. */
export function copySourcesOf(field: FieldDefinition): readonly CopyFromSource[] {
	const said = field.config.copyFrom;
	if (!said) return [];
	return Array.isArray(said) ? said : [said as CopyFromSource];
}

/**
 * `config.copyFrom = { link, field }` (ou uma lista delas): escolher o vínculo `link` traz o `field`
 * do registro escolhido para este campo — uma cópia, editável depois.
 *
 * O servidor também copia no salvar quando o campo vem vazio; isto é só para o
 * valor aparecer NA HORA em que o vínculo é escolhido. Mesma regra de `useAutofill`:
 * escreve só no vazio ou no que ele mesmo escreveu e ninguém editou, e reconfere
 * depois do `await`. Limpar o vínculo não apaga a cópia.
 *
 * Devolve, por campo, o rótulo do vínculo de onde veio o valor que ainda está lá
 * — o "copiado de …" sob o campo.
 */
export function useCopyFrom(input: {
	entity: () => EntityDefinition;
	values: Record<string, unknown>;
	/** O registro em edição; ausente = criação. */
	initial: () => Record<string, unknown> | undefined;
	/** Muda quando o MESMO formulário recomeça em branco (a linha "adicionar" depois de gravar): copia de novo. */
	round?: () => number;
	/**
	 * O valor de fábrica de um campo numa criação (o `false` de um interruptor, o `[]` de
	 * uma múltipla, o `config.default`): ninguém o disse, então a cópia passa por cima.
	 */
	factory?: (code: string) => unknown;
}): Record<string, string> {
	const copiedFrom = reactive<Record<string, string>>({});
	const port = inject(AUTOFILL_PORT, null);
	const read = port?.record?.bind(port);
	if (!read) return copiedFrom;

	const filledBy: Record<string, unknown> = {};
	// Numa edição, o valor com que o registro abriu também é "não dito" — a mesma
	// regra do trigger no UPDATE: trocar a ligação sem mexer no campo recopia.
	const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
	const untouched = (code: string) =>
		isEmpty(input.values[code]) ||
		(code in filledBy && input.values[code] === filledBy[code]) ||
		(input.initial() !== undefined
			? input.values[code] === input.initial()?.[code]
			: input.factory !== undefined && same(input.values[code], input.factory(code)));

	/** Os vínculos que alguém copia, com os campos que copiam de cada um. */
	const copies = () => {
		const fields = liveFields(input.entity());
		return fields
			.filter((link) => link.type === "ref_entity" && typeof link.config.entity === "string")
			.map((link) => ({
				link,
				targets: fields.flatMap((target) =>
					copySourcesOf(target)
						.filter((source) => source.link === link.code)
						.map((source) => ({ code: target.code, from: source.field })),
				),
			}))
			.filter((one) => one.targets.length > 0);
	};

	watch(
		() => ({ round: input.round?.(), ids: copies().map(({ link }) => input.values[link.code]) }),
		async ({ round, ids: now }, before) => {
			// Na abertura, só numa criação: o vínculo que já veio escolhido (defaults de
			// `form.create` ou da URL) mostra o valor antes do salvar. Editar nunca copia ao abrir.
			if (!before && input.initial()) return;
			const again = before !== undefined && round !== before.round;
			if (again) for (const code of Object.keys(filledBy)) delete filledBy[code];
			await Promise.all(
				copies().map(async ({ link, targets }, i) => {
					const id = now[i];
					if (isEmpty(id) || (!again && id === before?.ids[i])) return;
					const row = await read(String(link.config.entity), String(id));
					if (!row || input.values[link.code] !== id) return;
					for (const { code, from } of targets) {
						if (!(from in row) || !untouched(code)) continue;
						input.values[code] = row[from];
						filledBy[code] = row[from];
						copiedFrom[code] = link.label;
					}
				}),
			);
		},
		{ immediate: true },
	);

	// O "copiado de" sai quando a pessoa escreve por cima.
	watch(
		() => Object.keys(copiedFrom).map((code) => input.values[code]),
		() => {
			for (const code of Object.keys(copiedFrom))
				if (input.values[code] !== filledBy[code]) delete copiedFrom[code];
		},
	);

	return copiedFrom;
}
